/**
 * One URL scheme for a project's top tabs: `?tab=<name>` for every tab (2026-11-04). The project's own pages are
 * `?tab=home` (the Dashboard), `?tab=code` (Code at Home: the project's files, `&path=…&line=…`) and `?tab=logs`
 * (`&service=all|<name>|<a>,<b>`, `&at=`); the workspace views are `?tab=apps|files|api|data|…` (utils/workspaceModel).
 * The old `?view=dashboard|files|logs` (and `?view=dashboard&tab=<view>`) is still read, never written: the first sync
 * rewrites it to the `tab=` form with replace.
 *
 * Each tab owns some params (TAB_PARAMS, one registry for the Home modes and the workspace views): the element selected
 * in it, so Back, a reload or a copied link return to it. Switching tabs drops every other tab's (TAB_OWN_PARAMS), so
 * none of them is carried over (Logs → Apps used to leave `?view=logs&tab=apps&service=…`). A tab writes its own with
 * replace (replaceTabParams: no history entry per click), never a default, and drops one pointing at something gone.
 */

/** The Home mode (Canvas's dashboardOverviewMode) → its tab name in the URL. */
export const HOME_TAB_OF_MODE = { dashboard: 'home', files: 'code', logs: 'logs' };
/** The tab names (new, and the old `?tab=dashboard`) that are a Home mode. */
const MODE_OF_TAB = { home: 'dashboard', dashboard: 'dashboard', code: 'files', logs: 'logs' };
/** The old `?view=` values. */
const MODE_OF_OLD_VIEW = { dashboard: 'dashboard', files: 'files', logs: 'logs' };

/**
 * Each tab's own params (2026-11-04: every tab keeps its selected element), by the tab's name in the URL:
 * - home: none; code: the file and line (`path`, `line`, `preview` for a Markdown file's preview); logs: `service`, `at`;
 * - apps: the service and its sub-tab (`service`, `section`); files: the file (`path`);
 * - api: the service and the endpoint (`service`, `endpoint`: `GET /users/{id}`); mcp: the service and the tool (`tool`);
 * - data: the store and its table / collection / bucket / topic / label (`store`, `table`), or its Restore points
 *   (`restore`: a point's id, `all` for the list);
 * - traces: the request and its step (`trace`, `span`); quality: the service scope, the check and the finding (`service`, `check`, `finding`);
 * - jobs: the service and the job whose runs are open (`service`, `job`); config, git: the service (`service`);
 * - finops: the runtime and region the estimate is for (`runtime`, `region`; FinOps has no period to pick);
 * - changes: the commit (`commit`); activity: the services (`api,web`), the range and who (`service`, `when`,
 *   `what`); sessions: the session whose steps are open (`session`); mail (External Services): the filter and the
 *   message open (`status`, `message`); terminal: none (its shells are live, not linkable).
 */
export const TAB_PARAMS = {
  home: [],
  code: ['path', 'line', 'preview'],
  logs: ['service', 'at'],
  apps: ['service', 'section'],
  files: ['path'],
  api: ['service', 'endpoint'],
  mcp: ['service', 'tool'],
  data: ['store', 'table', 'restore'],
  traces: ['trace', 'span'],
  quality: ['service', 'check', 'finding'],
  jobs: ['service', 'job'],
  finops: ['runtime', 'region'],
  config: ['service'],
  mail: ['status', 'message'],
  terminal: [],
  git: ['service'],
  changes: ['commit'],
  activity: ['service', 'when', 'what'],
  sessions: ['session'],
};
/** Older params a tab no longer writes (Files' `&file=`), dropped with the others. */
const OLD_TAB_PARAMS = ['file', 'section'];
/** Every param that belongs to some tab: what switching tabs drops (except the new tab's own). */
export const TAB_OWN_PARAMS = [...new Set([...Object.values(TAB_PARAMS).flat(), ...OLD_TAB_PARAMS])];
/** The params of one tab, by its name in the URL (the old `dashboard` is home), [] for an unknown one. */
export const paramsOfTab = (tab) => TAB_PARAMS[tab === 'dashboard' ? 'home' : tab] || [];

/** Whether `?tab=` names one of the Home modes (home, code, logs, the old dashboard). */
export const isHomeTab = (tab) => Object.prototype.hasOwnProperty.call(MODE_OF_TAB, tab || '');

/**
 * The Home mode a URL asks for ('dashboard' | 'files' | 'logs'), or null: `?tab=home|code|logs` first, then the old
 * `?view=dashboard|files|logs` (also under a workspace `?tab=`: `?view=dashboard&tab=apps` lands on Home with Apps open).
 */
export function homeModeFromSearch(search) {
  const params = new URLSearchParams(search || '');
  const tab = params.get('tab');
  if (isHomeTab(tab)) { return MODE_OF_TAB[tab]; }
  return MODE_OF_OLD_VIEW[params.get('view')] || null;
}

/**
 * The search string for a Home mode: `?tab=<home|code|logs>`, the old `view` dropped, every other tab's own params
 * dropped (the mode's own kept). `mode` null (a service is shown): no Home tab at all.
 */
export function searchForHomeMode(search, mode) {
  const params = new URLSearchParams(search || '');
  params.delete('view');
  params.delete('history');
  if (params.get('focus') === 'jobs') { params.delete('focus'); }
  const keep = mode ? paramsOfTab(HOME_TAB_OF_MODE[mode]) : [];
  TAB_OWN_PARAMS.filter((key) => !keep.includes(key)).forEach((key) => params.delete(key));
  if (mode && HOME_TAB_OF_MODE[mode]) { params.set('tab', HOME_TAB_OF_MODE[mode]); } else if (isHomeTab(params.get('tab'))) { params.delete('tab'); }
  return searchOf(params);
}

/** `?…` with `tab` first (`?tab=logs&service=…`), '' when empty. */
export function searchOf(params) {
  const tab = params.get('tab');
  const ordered = new URLSearchParams();
  if (tab) { ordered.set('tab', tab); }
  params.forEach((value, key) => { if (key !== 'tab') { ordered.append(key, value); } });
  const text = ordered.toString();
  return text ? `?${text}` : '';
}

/** `?tab=…` for a link to a project's Home mode (Dashboard, Code's file, Logs on some services). */
export function homeTabQuery(mode, { path = null, line = null, service = null } = {}) {
  const params = new URLSearchParams();
  params.set('tab', HOME_TAB_OF_MODE[mode] || 'home');
  if (mode === 'files' && path) { params.set('path', path); }
  if (mode === 'files' && path && line) { params.set('line', String(line)); }
  if (mode === 'logs' && service) { params.set('service', service); }
  return params.toString();
}

/**
 * The address bar's query when the router's location is behind it (Canvas writes the workspace view with
 * history.pushState, which the router does not see), else the router's: what a URL writer must start from, or it puts
 * back params another writer already took out.
 */
export function liveSearch(location) {
  if (typeof window === 'undefined' || !location) { return location?.search || ''; }
  return window.location.pathname === location.pathname ? window.location.search : (location.search || '');
}

/** The tab the URL is on, by its name in the URL (`home` for the old `?tab=dashboard` and `?view=dashboard`), or null. */
export function tabOfSearch(search) {
  const params = new URLSearchParams(search || '');
  const tab = params.get('tab');
  if (isHomeTab(tab)) { return HOME_TAB_OF_MODE[MODE_OF_TAB[tab]]; }
  if (tab) { return tab; }
  const mode = MODE_OF_OLD_VIEW[params.get('view')];
  return mode ? HOME_TAB_OF_MODE[mode] : null;
}

/** A tab's own params in a URL, `{ [param]: value }` (only the ones there), `{}` when the URL is on another tab. */
export function tabParamsFromSearch(search, tab) {
  if (!tab || tabOfSearch(search) !== tab) { return {}; }
  const params = new URLSearchParams(search || '');
  const out = {};
  paramsOfTab(tab).forEach((key) => { if (params.get(key)) { out[key] = params.get(key); } });
  return out;
}

/**
 * The search string with a tab's selection written in: each of `values`' keys that is the tab's own param is set (a
 * non-empty string) or dropped (null, '', undefined); other keys are ignored. Only while the URL is on that tab: from
 * another tab the search comes back as it was.
 */
export function searchWithTabParams(search, tab, values = {}) {
  if (!tab || tabOfSearch(search) !== tab) { return search || ''; }
  const params = new URLSearchParams(search || '');
  const own = paramsOfTab(tab);
  Object.entries(values || {}).forEach(([key, value]) => {
    if (!own.includes(key)) { return; }
    if (value === null || value === undefined || value === '') { params.delete(key); } else { params.set(key, String(value)); }
  });
  return searchOf(params);
}

/**
 * Writes a tab's selection in the address bar with replace (a click is not a history entry), when it changes it and the
 * URL is on that tab. Returns whether it wrote.
 */
export function replaceTabParams(tab, values) {
  if (typeof window === 'undefined') { return false; }
  const now = window.location.search;
  const next = searchWithTabParams(now, tab, values);
  if (next === now || (!now && next === '')) { return false; }
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${next}${window.location.hash}`);
  return true;
}
